import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { Condition } from "@/types";

import type { ActivityPeriod } from "../ActionRecord";

export interface UseInvocationActivityOptions {
  /** ISO 8601, inclusive. */
  startTime: string;
  /** ISO 8601, exclusive. */
  endTime: string;
  /** Omit for the finest the window allows. */
  period?: ActivityPeriod;
  condition?: Condition;
  /** Milliseconds between refetches, or false. */
  refetchInterval?: number | false;
}

/**
 * Bucketed activity of the current org's invocations over a window. The condition is part of the
 * query key, so narrowing the filter fetches rather than redrawing stale buckets.
 */
export function useInvocationActivity({
  startTime,
  endTime,
  period,
  condition,
  refetchInterval = false,
}: UseInvocationActivityOptions) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: [
      "invocationActivity",
      currentOrganization?.org_id,
      startTime,
      endTime,
      period ?? null,
      condition ?? null,
    ],
    queryFn: async ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }

      return actionService.getInvocationActivity(
        currentOrganization.org_id,
        {
          start_time: startTime,
          end_time: endTime,
          period,
          condition,
        },
        { abortSignal: signal },
      );
    },
    enabled: currentOrganization !== null,
    refetchInterval,
    // A window that ends at "now" has a live rightmost bucket, so the app's five-minute
    // default would leave the chart's leading edge stale on a remount or a focus with
    // nothing saying so. Same reason as `useInvocationBreakdown` and
    // `useInvocationQueueStats`.
    staleTime: 0,
    // Keep the old series on screen while the new window loads, so a zoom does not flash empty.
    placeholderData: keepPreviousData,
  });
}
